import * as React from 'react';
import { track } from '../track';

// PostHog survey configuration
const SURVEY_ID = '0198f549-a3be-0000-d90f-06feba7c101a';

export function DocsRating(props: { path: string }) {
  const { path } = props;

  const stripped = path.replace(/\/+$/, '');
  const filename = stripped === '/learn' ? '/learn/index' : stripped;

  const [currentStep, setCurrentStep] = React.useState<'rating' | 'feedback' | 'complete'>('rating');
  const [ratingValue, setRatingValue] = React.useState<number | null>(null);
  const [feedbackText, setFeedbackText] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Track when survey is shown (on first render)
  React.useEffect(() => {
    track('survey shown', {
      $survey_id: SURVEY_ID,
      page_path: filename
    });
  }, [filename]);

  const submitSurveyResponse = async (responses: { [questionId: string]: string | number }) => {
    try {
      setIsSubmitting(true);

      // Build survey questions array with responses
      const surveyQuestions = [
        {
          id: '5688231f-7576-4118-be34-a029909f185e',
          question: 'Is this page helpful?',
          response: responses['$survey_response_5688231f-7576-4118-be34-a029909f185e']
        }
      ];

      // Add feedback question if it exists
      if (responses['$survey_response_6efcf86c-11f3-4701-9c84-5aec7303e23a']) {
        surveyQuestions.push({
          id: '6efcf86c-11f3-4701-9c84-5aec7303e23a',
          question: 'How can we improve it?',
          response: responses['$survey_response_6efcf86c-11f3-4701-9c84-5aec7303e23a']
        });
      }

      // Capture the "survey sent" event with PostHog in the correct format
      track('survey sent', {
        $survey_id: SURVEY_ID,
        $survey_name: 'Documentation feedback',
        $survey_questions: surveyQuestions,
        $survey_completed: true,
        page_path: filename,
        ratingValue: ratingValue,
        feedbackText: feedbackText,
        ...responses
      });
    } catch (error) {
      console.error('Error submitting survey response:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRatingSubmit = async (rating: number) => {
    setRatingValue(rating);

    // Rating scale: 1 = negative, 2 = neutral, 3 = positive
    // Based on branching logic: positive (3) goes to end, negative/neutral (1,2) go to feedback
    if (rating === 3) {
      // Positive rating - submit and complete
      await submitSurveyResponse({
        '$survey_response_5688231f-7576-4118-be34-a029909f185e': rating
      });
      setCurrentStep('complete');
    } else {
      // Negative or neutral - show feedback form
      setCurrentStep('feedback');
    }
  };

  const handleFeedbackSubmit = async () => {
    if (ratingValue === null) return;

    await submitSurveyResponse({
      '$survey_response_5688231f-7576-4118-be34-a029909f185e': ratingValue,
      '$survey_response_6efcf86c-11f3-4701-9c84-5aec7303e23a': feedbackText
    });
    setCurrentStep('complete');
  };

  const renderRatingQuestion = () => (
    <div style={{ textAlign: 'center' }}>
      <h3 style={{ textAlign: 'left', marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
        Is this page helpful?
      </h3>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '8px' }}>
        {[1, 2, 3].map((value) => (
          <button
            key={value}
            onClick={() => handleRatingSubmit(value)}
            disabled={isSubmitting}
            style={{
              background: 'none',
              border: '2px solid #e5e7eb',
              borderRadius: '50%',
              width: '60px',
              height: '60px',
              fontSize: '24px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseOver={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseOut={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            {value === 1 ? '😞' : value === 2 ? '😐' : '😊'}
          </button>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '14px',
          color: '#6b7280',
          maxWidth: '200px',
          margin: '0 auto'
        }}
      >
        <span>No</span>
        <span>Yes</span>
      </div>
    </div>
  );

  const renderFeedbackQuestion = () => (
    <div style={{ textAlign: 'center' }}>
      <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>How can we improve it?</h3>
      <textarea
        value={feedbackText}
        onChange={(e) => setFeedbackText(e.target.value)}
        placeholder="Please share your feedback..."
        disabled={isSubmitting}
        style={{
          width: '100%',
          minHeight: '100px',
          padding: '12px',
          border: '2px solid #e5e7eb',
          borderRadius: '8px',
          fontSize: '14px',
          fontFamily: 'inherit',
          resize: 'vertical',
          marginBottom: '16px',
          outline: 'none'
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = '#3b82f6';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = '#e5e7eb';
        }}
      />
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          onClick={() => setCurrentStep('rating')}
          disabled={isSubmitting}
          style={{
            padding: '8px 16px',
            border: '2px solid #e5e7eb',
            borderRadius: '6px',
            background: 'white',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Back
        </button>
        <button
          onClick={handleFeedbackSubmit}
          disabled={isSubmitting}
          style={{
            padding: '8px 16px',
            border: '2px solid #3b82f6',
            borderRadius: '6px',
            background: '#3b82f6',
            color: 'white',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </div>
  );

  const renderComplete = () => (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontSize: '24px', marginBottom: '8px' }}>✨</div>
      <p style={{ fontSize: '16px', fontWeight: '500', color: '#059669' }}>Thanks for letting us know!</p>
    </div>
  );

  return (
    <div
      style={{
        marginTop: '40px',
        marginBottom: '40px',
        display: 'flex'
      }}
    >
      {currentStep === 'rating' && renderRatingQuestion()}
      {currentStep === 'feedback' && renderFeedbackQuestion()}
      {currentStep === 'complete' && renderComplete()}
    </div>
  );
}
